import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";

import { COMPANY } from "@/lib/company";

const LAST_UPDATED = "August 7, 2026";

export default function PrivacyPolicyPage() {
  return (
    <div className="pt-28 md:pt-32 pb-20 md:pb-28">
      <div className="max-w-[980px] mx-auto px-5 md:px-10">
        <Link to="/" className="btn-ghost link-underline inline-flex">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Home
        </Link>

        <header className="mt-10 pb-10 border-b border-white/10">
          <p className="eyebrow">[ Privacy ]</p>
          <h1 className="hero-headline mt-5">Privacy Policy</h1>
          <p className="body-text mt-5 max-w-3xl">
            This Privacy Policy explains how {COMPANY.legalName} collects, uses,
            stores and protects personal information when you use this website or
            contact us through it.
          </p>
          <p className="text-xs uppercase tracking-[0.18em] text-brand-gray mt-5">
            Last updated: {LAST_UPDATED}
          </p>
        </header>

        <div className="mt-10 space-y-10">
          <LegalSection title="1. Who is responsible for your data?">
            <p>
              The data controller for information collected through this website is
              {` ${COMPANY.legalName}`}.
            </p>
            <p className="mt-3">
              Privacy enquiries and data-protection requests can be sent to{" "}
              <a
                href={`mailto:${COMPANY.email}`}
                className="link-underline text-brand-white hover:text-brand-yellow"
              >
                {COMPANY.email}
              </a>
              .
            </p>
          </LegalSection>

          <LegalSection title="2. Information we collect">
            <p>Depending on how you use the website, we may process:</p>
            <ul className="legal-list mt-4">
              <li>
                Contact information you submit, including your name and email address.
              </li>
              <li>
                Enquiry information, including project type and the message you send
                through the contact form.
              </li>
              <li>
                Technical and analytics information, such as page visits, device or
                session identifiers and interaction data, only where optional analytics
                have been enabled and you have given the required consent.
              </li>
              <li>
                Technical security and server information needed to operate, secure and
                troubleshoot the website.
              </li>
            </ul>
          </LegalSection>

          <LegalSection title="3. How we use personal information">
            <p>We use information collected through the website to:</p>
            <ul className="legal-list mt-4">
              <li>Receive, review and respond to project enquiries.</li>
              <li>Communicate with you about the enquiry you submitted.</li>
              <li>Maintain the website, protect it from misuse and resolve technical issues.</li>
              <li>
                Understand website usage and improve the site when you have consented
                to optional analytics.
              </li>
              <li>Comply with applicable legal obligations where required.</li>
            </ul>
          </LegalSection>

          <LegalSection title="4. Legal bases for processing">
            <p>
              The legal basis depends on the context. Processing may be necessary to
              take steps at your request before entering into a contract, to pursue
              legitimate interests in responding to enquiries and operating a secure
              website, to comply with a legal obligation, or to rely on your consent
              where consent is required.
            </p>
            <p className="mt-3">
              Optional analytics are not activated by this website until you choose to
              allow analytics. You can withdraw that choice at any time through Cookie
              Settings in the footer.
            </p>
          </LegalSection>

          <LegalSection title="5. Contact form information">
            <p>
              When you submit the contact form, the information you provide is stored in
              the website&apos;s administrative lead system so the enquiry can be managed.
              The website also attempts to send a notification of the enquiry to{" "}
              <a
                href={`mailto:${COMPANY.email}`}
                className="link-underline text-brand-white hover:text-brand-yellow"
              >
                {COMPANY.email}
              </a>
              .
            </p>
            <p className="mt-3">
              The form requires acknowledgement of this Privacy Policy before submission.
            </p>
          </LegalSection>

          <LegalSection title="6. Analytics and cookies">
            <p>
              Optional analytics may use PostHog to understand visits and website usage.
              Analytics remains disabled until you allow it through the consent banner or
              Cookie Settings. If you reject optional analytics, the website remains
              available and functional.
            </p>
            <p className="mt-3">
              For details about storage technologies, purposes and how to change your
              choice, see our{" "}
              <Link
                to="/cookie-policy"
                className="link-underline text-brand-white hover:text-brand-yellow"
              >
                Cookie Policy
              </Link>
              .
            </p>
          </LegalSection>

          <LegalSection title="7. Service providers and international processing">
            <p>
              Website hosting, infrastructure, analytics and email-delivery providers may
              process information on behalf of {COMPANY.legalName} when necessary to
              provide their services. Some providers may process data in countries other
              than your own. Where data-protection law requires safeguards for an
              international transfer, the applicable safeguards depend on the relevant
              provider arrangement and processing location.
            </p>
          </LegalSection>

          <LegalSection title="8. How long information is kept">
            <p>
              Personal information is retained only for as long as reasonably necessary
              for the purposes described in this policy, to manage the relevant enquiry,
              maintain appropriate business records, resolve disputes and meet applicable
              legal obligations. Retention may vary depending on the nature of the
              information and the relationship with the person who submitted it.
            </p>
          </LegalSection>

          <LegalSection title="9. Your data-protection rights">
            <p>
              Depending on the law that applies to you, you may have rights to request
              access to your personal information, correction, deletion, restriction of
              processing, objection to certain processing, and data portability. Where
              processing relies on consent, you may withdraw that consent without
              affecting the lawfulness of processing carried out before withdrawal.
            </p>
            <p className="mt-3">
              You may also have the right to lodge a complaint with the competent data
              protection supervisory authority in your country or region.
            </p>
          </LegalSection>

          <LegalSection title="10. How to exercise your rights">
            <p>
              Send a request to{" "}
              <a
                href={`mailto:${COMPANY.email}`}
                className="link-underline text-brand-white hover:text-brand-yellow"
              >
                {COMPANY.email}
              </a>
              . We may need to verify enough information to confirm that the request
              relates to you before acting on it.
            </p>
          </LegalSection>

          <LegalSection title="11. Security">
            <p>
              Reasonable technical and organisational measures are used to protect the
              information handled by the website. No internet transmission or storage
              system can be guaranteed to be completely secure.
            </p>
          </LegalSection>

          <LegalSection title="12. Changes to this policy">
            <p>
              This policy may be updated when website functionality, providers or legal
              requirements change. The date at the top of this page shows the current
              version.
            </p>
          </LegalSection>
        </div>

        <div className="mt-12 border-t border-white/10 pt-8 flex flex-wrap items-center gap-4">
          <a href={`mailto:${COMPANY.email}`} className="btn-primary">
            <Mail className="h-4 w-4" />
            Privacy enquiry
          </a>
          <Link to="/cookie-policy" className="btn-outline">
            Cookie Policy
          </Link>
        </div>
      </div>

      <LegalStyles />
    </div>
  );
}

function LegalSection({ title, children }) {
  return (
    <section>
      <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-brand-white">
        {title}
      </h2>
      <div className="body-text mt-4 leading-relaxed">{children}</div>
    </section>
  );
}

function LegalStyles() {
  return (
    <style>{`
      .legal-list {
        list-style: none;
        padding: 0;
        display: grid;
        gap: .7rem;
      }
      .legal-list li {
        position: relative;
        padding-left: 1.25rem;
      }
      .legal-list li::before {
        content: "";
        position: absolute;
        left: 0;
        top: .72em;
        width: .38rem;
        height: .38rem;
        background: #E9C947;
      }
    `}</style>
  );
}
